/**
 * The WhatsApp wizard's help text — one owner for every customer-facing sentence in the shell.
 *
 * ── THE COPY TRIO (design decision, 2026-09-01) ──────────────────────────────────────────────────
 * Every screen answers the same three questions, in the same order, in the same place:
 *
 *   whatThis     — what this screen is asking of you
 *   whatHappens  — what happens when you continue
 *   whatYouNeed  — what you need to have to hand
 *
 * A practice manager doing this once, alone, at 5pm, cannot ask us what a screen means. The trio is
 * the answer to "what am I looking at and can I finish this right now?", and it exists on EVERY
 * screen — including the terminal ones, where "nothing is expected of you" is itself the answer.
 *
 * ── WHY THE COPY LIVES HERE AND NOT IN THE COMPONENTS ────────────────────────────────────────────
 * Decision CQ3. Keyed by the IMPORTED step enum, so `whatsapp-wizard-copy.test.js` can walk the
 * real key set and fail on either half of the drift: a step with no copy (ships an empty help
 * panel) or a copy key with no step (someone renamed a screen and left its words behind). Copy
 * scattered across ten .vue files can only ever be checked by reading all ten.
 *
 * ── RULES FOR EDITING THIS FILE ──────────────────────────────────────────────────────────────────
 * 1. No jargon. Not "WABA", not "Embedded Signup", not "sender registration", not "Business
 *    Portfolio". The reader runs a dental practice.
 * 2. No invented specifics. Only facts the product actually guarantees:
 *      • one Facebook sign-in, about a minute;
 *      • WhatsApp's own review takes days and happens on their side;
 *      • the number itself is included — Teamio pays for it, the practice does not;
 *      • WhatsApp messages cost one credit each, from the credits they already have.
 *    Anything else — a total setup time, an approval rate, a turnaround promise — is a number we
 *    would be making up, and the practice would hold us to it.
 * 2a. NO NUMBER PRICE. EVER. (Saumil, 2026-09-02.) Teamio absorbs the cost of the WhatsApp number,
 *    so no screen states what one costs, quotes a monthly figure, or says the practice pays for it.
 *    The earlier copy promised "we show you its exact monthly price before anything is bought" —
 *    that was ported from the A2P wizard by mistake and is now false as well as against the standing
 *    rule that Twilio number prices are never customer-facing. The ONE money fact that stays is the
 *    per-message credit, which is real and unchanged. A sweep in whatsapp-wizard-copy.test.js
 *    enforces this across the copy trio, every step template, the confirm dialogs and the error
 *    sentences — all four homes, because a rule enforced in one is a rule half kept.
 * 3. Say who does what. The recurring worry is "how much of this is on me?", so every screen says
 *    plainly which part is ours and which is theirs.
 * 4. Never promise a step we have not built. During the driven phase the un-automated branches end
 *    with a person, and the copy says so rather than implying self-serve.
 */

import { WIZARD_STEP_KEYS } from './wizardSteps';

/**
 * key → { whatThis, whatHappens, whatYouNeed }.
 *
 * Keys MUST match `WIZARD_STEP_KEYS` exactly — the copy-completeness test asserts both directions.
 */
export const WIZARD_COPY = {
  intro: {
    whatThis:
      'WhatsApp lets your practice message patients in the app they already use every day. '
      + 'Their replies arrive in Conversations, right next to your text messages.',
    whatHappens:
      "We'll ask about the phone number you want patients to message and about your practice's "
      + 'Facebook account, then set up everything we can on our side. The one part only you can do '
      + 'is a single Facebook sign-in, which takes about a minute.',
    whatYouNeed:
      'The phone number you would like patients to message, and someone who can sign in to your '
      + "practice's Facebook account. WhatsApp messages use the credits you already have — one "
      + 'credit per message. If you need a new number, we set one up for you — it is included, at '
      + 'no extra charge to your practice.',
  },

  triage: {
    whatThis:
      'Which phone number your patients will message you on.',
    // The move is NOT seamless: WhatsApp genuinely stops working on that phone while the number is
    // switched over. Saying otherwise reads as a promise of no interruption and would be found out
    // on the day, by the practice, mid-switch. Say it plainly and say we agree the timing first.
    whatHappens:
      'Your answers decide the rest of the setup. If you need a number, we set one up for you. If '
      + 'the number you want is already live in the WhatsApp app on a phone, our team moves it '
      + 'across by hand and agrees the timing with you first, because WhatsApp stops working on '
      + 'that phone during the switch.',
    whatYouNeed:
      'The phone number you would like patients to use, and whether that number is already set up '
      + 'in the WhatsApp app on a phone.',
  },

  byo_ack: {
    whatThis:
      'The things that change when you move a WhatsApp number your practice already uses. Please '
      + 'read each one and tick it to show your practice understands and agrees.',
    whatHappens:
      'We save these confirmations alongside your answers, so there is a clear record that your '
      + 'practice agreed to the move before we start it. Nothing changes on your number yet — our '
      + 'team agrees a time with you first.',
    whatYouNeed:
      'A moment to read each point. Every box has to be ticked before you can continue, because '
      + 'moving a number you already use is not something we can undo partway through.',
  },

  confirm: {
    whatThis:
      'The name patients see on every WhatsApp message from you, and your confirmation that these '
      + 'patients agreed to hear from you.',
    whatHappens:
      'WhatsApp reviews this name before your account goes live, so use the name your patients '
      + 'already know you by. We save your answers and move on to your number.',
    whatYouNeed:
      'Your practice name as a patient would recognize it (between 3 and 512 characters), and '
      + 'confirmation that the patients you plan to message have given you permission to contact '
      + 'them.',
  },

  number: {
    whatThis:
      'The phone number your practice will send WhatsApp messages from.',
    whatHappens:
      'We set the number up for you and move straight on to connecting WhatsApp.',
    whatYouNeed:
      'Nothing to prepare. We suggest a number for you, and you can search by area code for a '
      + 'different one.',
  },

  handoff: {
    whatThis:
      'The part of this setup our team does by hand.',
    whatHappens:
      'We pass everything you have told us to our team and they carry on from here. You will hear '
      + 'from us directly.',
    // NOT "nothing right now": this screen has a button that hands the setup to a person, and on
    // the local-number branch it also offers the alternative. Telling a practice nothing is needed
    // from them, on a screen that is waiting for them to choose, is how a setup quietly stalls.
    whatYouNeed:
      'Nothing to prepare. All we need is your go-ahead, so we know to pick it up.',
  },

  connect: {
    whatThis:
      "One sign-in with your practice's Facebook account. That sign-in is what creates your "
      + 'WhatsApp Business account, and WhatsApp requires it to come from you.',
    whatHappens:
      'A Facebook window opens and walks you through creating the account — about a minute. When '
      + 'you finish, we take over and set up the rest.',
    whatYouNeed:
      "The Facebook login for your practice. We never see it and we cannot do this part for you. "
      + 'If someone outside the practice looks after your Facebook account, they are the one who '
      + 'needs to sign in, and this screen can give you a link to hand them. Facebook also texts a '
      + 'code to your Teamio WhatsApp number; Teamio receives it and reads it to you, so do the '
      + 'sign-in while you are on the phone with our team.',
  },

  progress: {
    whatThis:
      'Where your setup has got to.',
    whatHappens:
      'We register your number with WhatsApp and send your appointment reminder message for '
      + "approval. WhatsApp's review takes a few days and happens on their side, not ours.",
    whatYouNeed:
      'Nothing. You can close this page — we turn WhatsApp on for you as soon as WhatsApp approves '
      + 'it, and we are watching for problems in the meantime.',
  },

  live: {
    whatThis:
      'WhatsApp is switched on for your practice.',
    whatHappens:
      'Messages patients send you on WhatsApp appear in Conversations next to your text messages. '
      + 'To answer on WhatsApp, choose WhatsApp in the reply options at the top of the conversation.',
    // NOT "reminders are unchanged": once WhatsApp is on and the reminder template is approved,
    // the reminder pipeline can deliver over WhatsApp, so the old sentence promised the opposite of
    // what may actually happen. The exact automatic-switch behaviour is a Task 10 live-verify item,
    // so this promises NEITHER direction — it states what is now possible and offers the change.
    whatYouNeed:
      "Your appointment reminders can now reach patients on WhatsApp as well. If you'd like to "
      + 'change how reminders go out, just ask us.',
  },

  handoff_pending: {
    whatThis:
      'Your setup is with our team.',
    // "We will be in touch", not "we will email you": no channel has been promised anywhere in
    // this flow, and naming one commits us to a delivery path nothing in the product guarantees.
    whatHappens:
      'We have everything you told us and we are handling the parts that need a person. We will be '
      + 'in touch when there is something for you to do, or when WhatsApp is ready.',
    whatYouNeed:
      'Nothing right now.',
  },

  blocked: {
    whatThis:
      "WhatsApp messaging is not available in your practice's country, so there is nothing to set "
      + 'up here.',
    whatHappens:
      'Nothing changes. Your text messaging is unaffected and keeps working exactly as it does '
      + 'today.',
    whatYouNeed:
      'Nothing. If the country on your account is wrong, contact Teamio and we will correct it.',
  },
};

/** The three fields every step's copy must carry. Exported so the test names them once. */
export const COPY_FIELDS = ['whatThis', 'whatHappens', 'whatYouNeed'];

/**
 * The copy trio for a step key.
 *
 * Returns null for an unknown key rather than an empty trio: a missing panel is a visible bug and
 * gets fixed, whereas three empty strings render as a silently helpless screen. The
 * copy-completeness test makes the unknown-key case unreachable in shipped code.
 */
export function wizardCopy(key) {
  return Object.prototype.hasOwnProperty.call(WIZARD_COPY, key) ? WIZARD_COPY[key] : null;
}

/**
 * Step keys that have no copy, and copy keys that match no step — the two halves of drift.
 * Exported so the node-test asserts on data rather than reimplementing the comparison.
 */
export function copyGaps() {
  const copyKeys = Object.keys(WIZARD_COPY);
  return {
    missing: WIZARD_STEP_KEYS.filter((k) => !copyKeys.includes(k)),
    orphans: copyKeys.filter((k) => !WIZARD_STEP_KEYS.includes(k)),
  };
}
